import { ApiServiceWithSettings, Audience, Authentication } from '../../common'
import { AxiosPromise } from 'axios'
import {
  Catalog,
  Categories,
  Pagination,
  Products,
  Suggestions
} from '@one/types'
import qs from 'qs'
import ProductEditor = Catalog.Backoffice.ProductEditor
import Pager = Pagination.Pager
import { pickBy } from 'lodash-es'
import { LongTasksBackoffice } from '../../LongTasksBackoffice'

export class BackofficeCatalogApi extends ApiServiceWithSettings {
  getProductBySlug(slug: string): AxiosPromise<Products.Product> {
    return this.get('/products/by-slug', {
      params: {
        slug
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getProduct(id: string): AxiosPromise<Products.Product> {
    return this.get('/products/by-sku', {
      params: {
        id
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getProductByIndexOrId(
    idOrIndex: string
  ): AxiosPromise<Array<Products.Product>> {
    return this.get('/products/list-by-id-or-index', {
      params: {
        idOrIndex
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getProductByManufacturerIndex(
    manufacturerIndex: string
  ): AxiosPromise<Array<Products.Product>> {
    return this.get('/products/list-by-manufacturer-index', {
      params: {
        manufacturerIndex
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getProductsListById(
    allowMissing: boolean,
    id: Array<string>
  ): AxiosPromise<Array<Products.Product>> {
    return this.get('/products/list-by-sku', {
      params: {
        allowMissing,
        id
      },
      paramsSerializer: function(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' })
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getProducts(
    pageNumber: number,
    pageSize: number,
    category: string,
    filtersQuery: Array<string>,
    price: string,
    q: string,
    sortCriteria: Products.SortingOptionCode
  ): AxiosPromise<Products.Backoffice.Responses.ProductsResult> {
    return this.get('/products/search/items', {
      params: {
        pageNumber,
        pageSize,
        price,
        q,
        sortCriteria,
        filtersQuery,
        category
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE,
      paramsSerializer: function(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' })
      }
    })
  }

  getFilters(page?: Products.Requests.FiltersPagination): AxiosPromise {
    return this.get('/products/search/filters', {
      params: {
        category: page && page.category ? page.category : undefined,
        filtersQuery:
          page && page.filtersQuery && page.filtersQuery.length > 0
            ? page.filtersQuery
            : undefined,
        q: page && page.q ? page.q : '*',
        price: page && page.price ? page.price : undefined
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE,
      paramsSerializer: function(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' })
      }
    })
  }

  getSuggestions(
    query: string
  ): AxiosPromise<Suggestions.Responses.Suggestion> {
    return this.get('/suggestions', {
      params: {
        userQuery: query
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getSynonyms(): AxiosPromise<Array<Catalog.Backoffice.SynonymDto>> {
    return this.get('/synonyms', {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE,
      cache: {
        ttl: 60 * 100,
        tags: ['synonyms']
      }
    })
  }

  createSynonym(synonym: Catalog.Backoffice.SynonymDto): AxiosPromise<void> {
    this.invalidate(['synonyms'])
    return this.post('/synonyms', synonym, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  deleteSynonym(synonym: Catalog.Backoffice.SynonymDto): AxiosPromise<void> {
    this.invalidate(['synonyms'])
    return this.delete('/synonyms', {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE,
      data: synonym
    })
  }

  getSynonymByName(
    name: string
  ): AxiosPromise<Array<Catalog.Backoffice.SynonymDto>> {
    return this.get(`/synonyms/${name}`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getAllCharacteristics(
    nameFilter: string | null,
    pageNumber: number,
    pageSize: number
  ): AxiosPromise<Catalog.Backoffice.Responses.CharacteristicsPager> {
    return this.get('/characteristics', {
      params: {
        nameFilter: nameFilter || null,
        pageNumber,
        pageSize
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE,
      cache: {
        tags: ['characteristics']
      }
    })
  }

  getAllCharacteristicValues(
    code: string | null,
    pageNumber: number,
    pageSize: number,
    valueFilter: string | null
  ): AxiosPromise<Catalog.Backoffice.Responses.CharacteristicValuesPager> {
    return this.get(`/characteristics/${code}/values`, {
      params: {
        pageNumber,
        pageSize,
        valueFilter: valueFilter || null
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE,
      cache: {
        tags: ['characteristics']
      }
    })
  }

  updateCharacteristic(
    body: Catalog.Backoffice.Requests.CharacteristicNameBody,
    code: string | null
  ): AxiosPromise<void> {
    this.invalidate(['characteristics'])
    return this.post(`/characteristics/${code}`, body, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  updateCharacteristicValue(
    body: Catalog.Backoffice.Requests.CharacteristicValueBody,
    code: string | null,
    valueCode: string | null
  ): AxiosPromise<void> {
    this.invalidate(['characteristics'])
    return this.post(`/characteristics/${code}/values/${valueCode}`, body, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  createCharacteristicValue(
    body: Catalog.Backoffice.Requests.CreateCharacteristicValueBody,
    code: string
  ): AxiosPromise<void> {
    this.invalidate(['characteristics'])
    return this.post(`/characteristics/${code}/values`, body, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getCategories(): AxiosPromise<Array<Categories.Category>> {
    return this.get('/categories/list', {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getProductSegments(
    pageNumber: number,
    pageSize: number
  ): AxiosPromise<Array<Catalog.Backoffice.Responses.SegmentsPager>> {
    return this.get('/product-segments', {
      params: {
        pageNumber,
        pageSize
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  createProductSegment(
    assignment: Catalog.Backoffice.Segment
  ): AxiosPromise<void> {
    return this.post('/product-segments', assignment, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getProductSegment(
    label: string
  ): AxiosPromise<Array<Catalog.Backoffice.Segment>> {
    return this.get(`/product-segments/by-label/${label}`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  updateProductSegment(
    label: string,
    assignment: Catalog.Backoffice.SegmentsList
  ): AxiosPromise<void> {
    return this.put(`/product-segments/by-label/${label}`, assignment, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  deleteProductSegment(label: string): AxiosPromise<void> {
    return this.delete(`/product-segments/by-label/${label}`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getGlobalProductSegment(): AxiosPromise<
    Array<Catalog.Backoffice.SegmentsLabel>
  > {
    return this.get(`/product-segments/public-products-label/`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  setGlobalProductSegment(label: string): AxiosPromise<void> {
    return this.put(
      `/product-segments/public-products-label/`,
      { label },
      {
        authentication: Authentication.REQUIRED,
        aud: Audience.BACKOFFICE
      }
    )
  }

  getLabelsList(): AxiosPromise<Array<string>> {
    return this.get(`/labels/list/`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE,
      cache: {
        tags: ['labels']
      }
    })
  }

  getDynamicParametersProductSeo(): AxiosPromise<
    Catalog.Backoffice.Seo.ProductSeoDefaultsDynamicParameters
  > {
    return this.get(`/seo/product/default/variables`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getMeasurementUnits(): AxiosPromise<
    Array<Catalog.Backoffice.Responses.MeasurementUnit>
  > {
    return this.get(`/dictionaries/measurement-units`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  createMeasurementUnit(
    payload: Catalog.Backoffice.Requests.CreateMeasurementUnit
  ): AxiosPromise<void> {
    return this.post('/dictionaries/measurement-units', payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  updateMeasurementUnit(
    measurementUnitId: string,
    payload: Catalog.Backoffice.Requests.CreateMeasurementUnit
  ): AxiosPromise<void> {
    return this.put(
      `/dictionaries/measurement-units/${measurementUnitId}`,
      payload,
      {
        authentication: Authentication.REQUIRED,
        aud: Audience.BACKOFFICE
      }
    )
  }

  deleteMeasurementUnit(measurementUnitId: string): AxiosPromise<void> {
    return this.delete(`/dictionaries/measurement-units/${measurementUnitId}`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getVatRates({
    pageNumber,
    pageSize
  }: Pagination.Page): AxiosPromise<
    Pagination.Pager<Catalog.Backoffice.Responses.VatRate>
  > {
    return this.get('/dictionaries/vat-rates', {
      params: {
        pageNumber,
        pageSize
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  createVatRate(
    payload: Catalog.Backoffice.Requests.CreateVatRate
  ): AxiosPromise<void> {
    return this.post('/dictionaries/vat-rates', payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  updateVatRate(
    vatRateId: string,
    payload: Catalog.Backoffice.Requests.CreateVatRate
  ): AxiosPromise<void> {
    return this.put(`/dictionaries/vat-rates/${vatRateId}`, payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  deleteVatRate(vatRateId: string): AxiosPromise<void> {
    return this.delete(`/dictionaries/vat-rates/${vatRateId}`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getManufacturers({
    pageNumber,
    pageSize,
    search
  }: {
    pageNumber: number
    pageSize: number
    search: string
  }): AxiosPromise<
    Pagination.Pager<Catalog.Backoffice.Responses.Manufacturer>
  > {
    return this.get('/dictionaries/manufacturers', {
      params: {
        pageNumber,
        pageSize,
        search
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  createManufacturers(
    payload: Catalog.Backoffice.Requests.CreateManufacturer
  ): AxiosPromise<void> {
    return this.post('/dictionaries/manufacturers', payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  updateManufacturers(
    manufacturerId: string,
    payload: Catalog.Backoffice.Requests.CreateManufacturer
  ): AxiosPromise<void> {
    return this.put(`/dictionaries/manufacturers/${manufacturerId}`, payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  deleteManufacturers(manufacturerId: string): AxiosPromise<void> {
    return this.delete(`/dictionaries/manufacturers/${manufacturerId}`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  createNewDraftProduct(
    payload: Catalog.Backoffice.Requests.CreateDraftProduct
  ): AxiosPromise<Catalog.Backoffice.Responses.DraftProductCreateResult> {
    return this.post(`/draft-products`, payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  sendProductActivation(productId: string): AxiosPromise<void> {
    return this.post(
      '/products/send-for-activation',
      {},
      {
        params: {
          productId
        },
        authentication: Authentication.REQUIRED,
        aud: Audience.BACKOFFICE
      }
    )
  }

  getProductsByIdentity(
    products: Catalog.Backoffice.ProductIdentityRequest
  ): AxiosPromise<Catalog.Backoffice.ProductIdentityRestView> {
    return this.post(
      '/products/by-identity',
      {
        ...products
      },
      {
        authentication: Authentication.REQUIRED,
        aud: Audience.BACKOFFICE
      }
    )
  }

  getFiltersGeneric(
    code: string,
    categoryId?: string | null,
    filtersQuery?: string[] | null,
    price?: string | null,
    q?: string | null
  ): AxiosPromise<void> {
    return this.get(`/products/search/filters/generic/${code}`, {
      params: {
        categoryId: categoryId || null,
        filtersQuery: filtersQuery || null,
        price: price || null,
        q: q || null
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getValuesForGenericFilterOrAttribute(
    code: string
  ): AxiosPromise<Array<{ value: string; valueCode: string }>> {
    return this.get(`/products/search/filters/${code}`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getProductFromEditor(
    id: string
  ): AxiosPromise<Catalog.Backoffice.ProductEditor.Product> {
    return this.get('/editor/product/by-sku', {
      params: {
        id
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getProductListFromEditor(
    query: ProductEditor.ProductListQuery
  ): AxiosPromise<ProductEditor.ProductSimpleResponsePager> {
    return this.get(`/editor/products`, {
      params: pickBy({
        ...query
      }),
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE,
      paramsSerializer: function(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' })
      }
    })
  }

  upsertProductInEditor(
    payload: Catalog.Backoffice.ProductEditor.UpsertProduct
  ) {
    return this.patch(`/editor/product`, payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  publishProductInEditor(
    productId: string
  ): AxiosPromise<Catalog.Backoffice.ProductEditor.ProductPublishResult> {
    return this.post(
      `/editor/product/publish`,
      {},
      {
        params: {
          productId
        },
        authentication: Authentication.REQUIRED,
        aud: Audience.BACKOFFICE
      }
    )
  }

  rejectChangesProductInEditor(productId: string) {
    return this.delete(`/editor/product`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE,
      params: {
        productId
      }
    })
  }

  bulkEditProductsInEditor(
    payload: Catalog.Backoffice.ProductEditor.ProductBulkEditRequest
  ) {
    return this.post(`/editor/products/bulk/edit`, payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  bulkDeleteProductsInEditor(
    payload: Catalog.Backoffice.ProductEditor.ProductFilters
  ) {
    return this.post(`/editor/products/bulk/delete`, payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getExampleImportProductsFile(): AxiosPromise<string> {
    return this.get(`/editor/products/import-file`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE,
      responseType: 'blob'
    })
  }

  initImportProducts(payload: { fileName: string }) {
    return this.post(`/editor/products/import/init-upload`, payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  startImportProducts(payload: { fileKey: string }) {
    return this.post(`/editor/products/import/start`, payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  /**
   * @deprecated
   * Use:
   * 1. initImportProducts
   * 2. LongTasksBackoffice.uploadAttachment
   * 3. LongTasksBackoffice.finishUpload
   * 4. startImportProducts
   */
  importProducts(
    formData: FormData
  ): AxiosPromise<Catalog.Backoffice.ProductEditor.ProductImportFinalResult> {
    return this.post(`/editor/products/import`, formData, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  beginPhotoUpload(payload: { productId: string; filename: string }) {
    return this.post(`/editor/begin-photo-upload`, payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getCategoriesTreeFromEditor(): AxiosPromise<
    Catalog.Backoffice.ProductEditor.CategoriesTreeResponse
  > {
    return this.get(`/editor/categories/tree`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getCategoryFromEditor(
    id: string
  ): AxiosPromise<Catalog.Backoffice.ProductEditor.Category> {
    return this.get(`/editor/category`, {
      params: {
        id
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  createCategoryInEditor(
    payload: Catalog.Backoffice.ProductEditor.CreateCategory
  ) {
    return this.post(`/editor/category/create`, payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE,
      params: {
        id: payload.id
      }
    })
  }

  editCategoryInEditor(payload: Catalog.Backoffice.ProductEditor.EditCategory) {
    return this.patch(`/editor/category/edit`, payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE,
      params: {
        id: payload.id
      }
    })
  }

  moveCategoryInEditor(payload: Catalog.Backoffice.ProductEditor.MoveCategory) {
    return this.patch(`/editor/category/move`, payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE,
      params: {
        id: payload.id
      }
    })
  }

  deleteCategoryInEditor(id: string) {
    return this.delete(`/editor/category`, {
      params: {
        id
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  publishChangesCategoriesInEditor() {
    return this.post(
      `/editor/categories/publish`,
      {},
      {
        authentication: Authentication.REQUIRED,
        aud: Audience.BACKOFFICE
      }
    )
  }

  rejectChangesCategoriesInEditor() {
    return this.delete(`/editor/categories/changes`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getCategoryList(
    query: ProductEditor.CategoryListQuery
  ): AxiosPromise<ProductEditor.CategorySimpleResponsePager> {
    return this.get(`/categories/database-search`, {
      params: {
        ...query
      },
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getAttributeFromEditor(id: string): AxiosPromise<ProductEditor.Attribute> {
    return this.get(`/editor/attributes/${id}`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getAttributeListFromEditor(
    query: ProductEditor.AttributeListQuery
  ): AxiosPromise<ProductEditor.AttributeResponsePager> {
    return this.get(`/editor/attributes`, {
      params: pickBy({
        ...query
      }),
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE,
      paramsSerializer: function(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' })
      }
    })
  }

  createAttributeInEditor(payload: ProductEditor.Attribute) {
    return this.post(`/editor/attributes`, payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  upsertAttributeInEditor(payload: ProductEditor.Attribute) {
    return this.put(`/editor/attributes`, payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  deleteAttributeInEditor(id: string) {
    return this.delete(`/editor/attributes/${id}`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  deleteAttributesInEditor(ids: string[]) {
    return this.delete(`/editor/attributes`, {
      data: ids,
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getAttributeClassFromEditor(
    id: string
  ): AxiosPromise<ProductEditor.AttributeClass> {
    return this.get(`/editor/attribute-classes/${id}`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getAttributeClassListFromEditor(
    query: ProductEditor.AttributeClassListQuery
  ): AxiosPromise<ProductEditor.AttributeClassSimpleResponsePager> {
    return this.get(`/editor/attribute-classes`, {
      params: pickBy({
        ...query
      }),
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE,
      paramsSerializer: function(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' })
      }
    })
  }

  createAttributeClassInEditor(payload: ProductEditor.CreateAttributeClass) {
    return this.post(`/editor/attribute-classes`, payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  editAttributeClassInEditor(payload: ProductEditor.EditAttributeClass) {
    return this.put(`/editor/attribute-classes/${payload.id}`, payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  deleteAttributeClassInEditor(id: string) {
    return this.delete(`/editor/attribute-classes/${id}`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  deleteAttributeClassesInEditor(ids: string[]) {
    return this.delete(`/editor/attribute-classes`, {
      data: ids,
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getRelationFromEditor(
    id: string
  ): AxiosPromise<ProductEditor.RelationByAttribute> {
    return this.get(`/editor/relations/${id}`, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  getRelationListFromEditor(
    query: ProductEditor.RelationByAttributeListQuery
  ): AxiosPromise<ProductEditor.RelationByAttributeResponsePager> {
    return this.get(`/editor/relations`, {
      params: pickBy({
        ...query
      }),
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE,
      paramsSerializer: function(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' })
      }
    })
  }

  upsertRelationInEditor(payload: ProductEditor.UpsertRelationByAttribute) {
    return this.put(`/editor/relations`, payload, {
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  deleteRelationsInEditor(ids: string[]) {
    return this.delete(`/editor/relations`, {
      data: ids,
      authentication: Authentication.REQUIRED,
      aud: Audience.BACKOFFICE
    })
  }

  longTasks = new LongTasksBackoffice(this)
}
